import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import { IconPicker } from '../../components/IconPicker';
import * as Icons from '@ant-design/icons';
import { withDevModalFilePath, HexColorPicker } from '../../components/common';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/settings/DocumentCategoriesTab.tsx');

type DocumentTypeRow = {
  id: string;
  name: string;
  category: 'general' | 'official';
  cycleMonths?: number | null;
  isSystem?: boolean;
  isEnabled?: boolean;
  color?: string | null;
  icon?: string | null;
  relatedEntityType?:
    | 'location'
    | 'location_building'
    | 'equipment'
    | 'part'
    | 'supplier'
    | 'user'
    | 'purchase_order'
    | null;
  readRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
  writeRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
};

const categoryLabels: Record<string, string> = {
  general: '一般',
  official: '法定点検',
};

const relatedTypeLabels: Record<string, string> = {
  location: 'エリア',
  location_building: 'エリア（建物）',
  equipment: '設備',
  part: '部品',
  supplier: '業者',
  user: 'ユーザー',
  purchase_order: '発注',
};

const roleLabels: Record<string, string> = {
  viewer: '閲覧のみ',
  user: 'ユーザー',
  manager: 'マネージャー',
  admin: '管理者',
};

const normalizeHexColor = (value?: string | null) => {
  if (!value) return undefined;
  if (/^#[0-9A-Fa-f]{8}$/.test(value)) {
    return value.slice(0, 7);
  }
  return value;
};

const DocumentCategoriesTab: React.FC = () => {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const utils = trpc.useUtils();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState<DocumentTypeRow | null>(null);
  const [form] = Form.useForm();
  const selectedCategory = Form.useWatch('category', form) as DocumentTypeRow['category'] | undefined;

  const { data: profile } = trpc.user.getProfile.useQuery();
  const canManage = ['admin', 'manager'].includes(profile?.role ?? '');

  const { data: documentTypes = [], isLoading } = trpc.document.listTypes.useQuery();
  const rows = documentTypes as DocumentTypeRow[];

  const createMutation = trpc.document.createType.useMutation({
    onSuccess: async () => {
      message.success('書類カテゴリを作成しました');
      setCreateOpen(false);
      form.resetFields();
      await utils.document.listTypes.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '書類カテゴリの作成に失敗しました');
    },
  });

  const updateMutation = trpc.document.updateType.useMutation({
    onSuccess: async () => {
      message.success('書類カテゴリを更新しました');
      setEditOpen(false);
      setSelected(null);
      form.resetFields();
      await utils.document.listTypes.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '書類カテゴリの更新に失敗しました');
    },
  });

  const toggleMutation = trpc.document.setTypeEnabled.useMutation({
    onSuccess: async () => {
      await utils.document.listTypes.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '有効化設定の更新に失敗しました');
    },
  });

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ color: '#ffffff', category: 'general', readRole: 'user', writeRole: 'user' });
    setCreateOpen(true);
  };

  const openEdit = React.useCallback((record: DocumentTypeRow) => {
    setSelected(record);
    form.setFieldsValue({
      name: record.name,
      category: record.category,
      relatedEntityType: record.relatedEntityType ?? (record.category === 'official' ? 'location_building' : undefined),
      cycleMonths: record.cycleMonths ?? undefined,
      color: normalizeHexColor(record.color) ?? '#ffffff',
      icon: record.icon ?? undefined,
      readRole: record.readRole ?? 'user',
      writeRole: record.writeRole ?? 'user',
    });
    setEditOpen(true);
  }, [form]);

  React.useEffect(() => {
    if (selectedCategory === 'official') {
      form.setFieldsValue({ relatedEntityType: 'location_building' });
    }
  }, [selectedCategory, form]);

  const relatedTypeOptions = useMemo(() => {
    return [
      { label: 'エリア', value: 'location' },
      { label: 'エリア（建物）', value: 'location_building' },
      { label: '設備', value: 'equipment' },
      { label: '部品', value: 'part' },
      { label: '業者', value: 'supplier' },
    ];
  }, []);

  const handleCreate = async () => {
    const values = await form.validateFields();
    const resolvedColor = typeof values.color === 'string' ? values.color : values.color?.toHexString();
    const colorValue = normalizeHexColor(resolvedColor) ?? '#ffffff';
    createMutation.mutate({
      name: values.name,
      category: values.category,
      relatedEntityType: values.relatedEntityType,
      readRole: values.readRole,
      writeRole: values.writeRole,
      cycleMonths: values.cycleMonths ?? undefined,
      color: colorValue,
      icon: values.icon,
    });
  };

  const handleUpdate = async () => {
    if (!selected) return;
    const values = await form.validateFields();
    const resolvedColor = typeof values.color === 'string' ? values.color : values.color?.toHexString();
    const colorValue = normalizeHexColor(resolvedColor ?? selected.color) ?? '#ffffff';
    if (selected.isSystem) {
      updateMutation.mutate({
        id: selected.id,
        color: colorValue,
        icon: values.icon ?? undefined,
      });
      return;
    }
    updateMutation.mutate({
      id: selected.id,
      name: values.name,
      category: values.category,
      relatedEntityType: values.relatedEntityType ?? selected.relatedEntityType ?? undefined,
      readRole: values.readRole ?? selected.readRole ?? 'user',
      writeRole: values.writeRole ?? selected.writeRole ?? 'user',
      cycleMonths: values.cycleMonths ?? undefined,
      color: colorValue,
      icon: values.icon,
    });
  };

  const getIconComponent = React.useCallback((iconName?: string | null) => {
    if (!iconName) return null;
    const iconKey = iconName as keyof typeof Icons;
    const IconComponent = Icons[iconKey] as React.ComponentType<{ style?: React.CSSProperties }> | undefined;
    return IconComponent ? <IconComponent style={{ fontSize: 16 }} /> : null;
  }, []);

  const columns: ColumnsType<DocumentTypeRow> = useMemo(
    () => [
      {
        title: '表示',
        key: 'appearance',
        width: 120,
        render: (_: unknown, record) => (
          <Space size="small">
            {getIconComponent(record.icon)}
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: 2,
                backgroundColor: record.color ?? token.colorFillSecondary,
                border: `1px solid ${token.colorBorder}`,
                display: 'inline-block',
              }}
            />
          </Space>
        ),
      },
      {
        title: 'カテゴリ名',
        dataIndex: 'name',
        key: 'name',
        render: (value: string) => <Text strong>{value}</Text>,
      },
      {
        title: '区分 / 関連',
        key: 'categoryRelated',
        render: (_: unknown, record) => {
          const categoryTag = (
            <Tag color={record.category === 'official' ? 'red' : 'default'}>
              {categoryLabels[record.category] ?? record.category}
            </Tag>
          );
          const relatedValue = record.relatedEntityType;
          if (!relatedValue) {
            return (
              <Space size={4} wrap={false}>
                {categoryTag}
                <Text type="secondary">未設定</Text>
              </Space>
            );
          }
          const label = relatedValue === 'location' && record.category === 'official'
            ? 'エリア（建物）'
            : relatedTypeLabels[relatedValue] ?? relatedValue;
          return (
            <Space size={4} wrap={false}>
              {categoryTag}
              <Tag>{label}</Tag>
            </Space>
          );
        },
      },
      {
        title: '権限',
        key: 'roles',
        render: (_: unknown, record) => (
          <Space size={4} wrap={false}>
            <Tag>{`読取:${roleLabels[record.readRole ?? 'user'] ?? record.readRole ?? '-'}`}</Tag>
            <Tag color="blue">{`書込:${roleLabels[record.writeRole ?? 'user'] ?? record.writeRole ?? '-'}`}</Tag>
          </Space>
        ),
      },
      {
        title: '提出周期',
        dataIndex: 'cycleMonths',
        key: 'cycleMonths',
        render: (value?: number | null) => (value ? `${value}ヶ月` : '-'),
      },
      {
        title: '種別',
        dataIndex: 'isSystem',
        key: 'isSystem',
        render: (value?: boolean) => (
          <Tag color={value ? 'blue' : 'default'}>
            {value ? 'システム' : 'カスタム'}
          </Tag>
        ),
      },
      {
        title: '有効',
        dataIndex: 'isEnabled',
        key: 'isEnabled',
        render: (_: unknown, record) => (
          <Switch
            checked={record.isSystem ? true : record.isEnabled}
            onChange={(checked) => toggleMutation.mutate({ documentTypeId: record.id, isEnabled: checked })}
            disabled={!canManage || record.isSystem}
          />
        ),
      },
      {
        title: '操作',
        key: 'action',
        render: (_: unknown, record) => (
          <Button
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
            disabled={!canManage}
          >
            編集
          </Button>
        ),
      },
    ],
    [canManage, getIconComponent, openEdit, toggleMutation, token]
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title="書類カテゴリ"
        extra={(
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!canManage}>
            新規作成
          </Button>
        )}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text type="secondary">
            法定点検の3種は固定表示です。カスタムカテゴリは必要に応じて有効化してください。
          </Text>
          <Table
            rowKey={(record) => record.id}
            dataSource={rows}
            columns={columns}
            pagination={false}
            loading={isLoading}
            size="small"
          />
        </Space>
      </Card>

      <Modal
        title="書類カテゴリを作成"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void handleCreate()}
        okText="作成"
        cancelText="キャンセル"
        confirmLoading={createMutation.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="カテゴリ名"
            rules={[{ required: true, message: 'カテゴリ名を入力してください' }]}
          >
            <Input placeholder="例: 契約書" maxLength={255} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="category"
                label="区分"
                rules={[{ required: true, message: '区分を選択してください' }]}
              >
                <Select
                  options={[
                    { label: '一般', value: 'general' },
                    { label: '法定点検', value: 'official' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="relatedEntityType"
                label="関連タイプ"
                rules={[{ required: true, message: '関連タイプを選択してください' }]}
              >
                <Select
                  options={relatedTypeOptions}
                  disabled={selectedCategory === 'official'}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="readRole"
                label="読取権限"
                rules={[{ required: true, message: '読取権限を選択してください' }]}
              >
                <Select
                  options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="writeRole"
                label="書き込み権限"
                rules={[{ required: true, message: '書き込み権限を選択してください' }]}
              >
                <Select
                  options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="cycleMonths" label="提出周期（任意）">
            <InputNumber min={1} max={120} style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="color" label="カラー">
                <HexColorPicker />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="icon" label="アイコン">
                <IconPicker placeholder="アイコンを選択（任意）" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="書類カテゴリを編集"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        onOk={() => void handleUpdate()}
        okText="保存"
        cancelText="キャンセル"
        confirmLoading={updateMutation.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="カテゴリ名"
            rules={[{ required: true, message: 'カテゴリ名を入力してください' }]}
          >
            <Input placeholder="例: 契約書" maxLength={255} disabled={selected?.isSystem} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="category"
                label="区分"
                rules={[{ required: true, message: '区分を選択してください' }]}
              >
                <Select
                  options={[
                    { label: '一般', value: 'general' },
                    { label: '法定点検', value: 'official' },
                  ]}
                  disabled={selected?.isSystem}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="relatedEntityType"
                label="関連タイプ"
                rules={[{ required: true, message: '関連タイプを選択してください' }]}
              >
                <Select
                  options={relatedTypeOptions}
                  disabled={selected?.isSystem || Boolean(selected?.relatedEntityType) || selectedCategory === 'official'}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="readRole"
                label="読取権限"
                rules={[{ required: true, message: '読取権限を選択してください' }]}
              >
                <Select
                  options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))}
                  disabled={selected?.isSystem}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="writeRole"
                label="書き込み権限"
                rules={[{ required: true, message: '書き込み権限を選択してください' }]}
              >
                <Select
                  options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))}
                  disabled={selected?.isSystem}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="cycleMonths" label="提出周期（任意）">
            <InputNumber min={1} max={120} style={{ width: '100%' }} disabled={selected?.isSystem} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="color" label="カラー">
                <HexColorPicker />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="icon" label="アイコン">
                <IconPicker placeholder="アイコンを選択（任意）" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Space>
  );
};

export default DocumentCategoriesTab;
